import React, { useEffect, useState, useCallback } from "react";
import FilterBar, { computeRange, useSharedFilter } from "../FilterBar";
import { api, buildParams } from "../api";
import { Panel, Skeleton, Badge, EmptyState, ErrorState, Button, PageHeader, fmtNum, fmtDate } from "../Primitives";
import { Pagination } from "./Signals";
import { PROVIDER_NAMES, platformLabel, EVENT_TYPE_LABELS } from "../catalog";
import { Search, Download } from "lucide-react";

const fmt = (iso) => fmtDate(iso);

const defaultFilter = () => ({
  preset: "7d", ...computeRange("7d"),
  platform: "", provider: "", game: "", device: "", event_type: "", campaign: "",
});

export default function EventsHistory() {
  const [filter, setFilter] = useSharedFilter(defaultFilter);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: "server_ts", order: "desc" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/events", {
        params: {
          ...buildParams(filter), search: search || undefined,
          sort: sort.field, order: sort.order, page, page_size: 25,
        },
      });
      setData(data);
    } catch (e) { setError("Não foi possível carregar os eventos."); } finally { setLoading(false); }
  }, [filter, search, sort, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [filter, search, sort]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/events/export", {
        params: { ...buildParams(filter), search: search || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `mario-ia-eventos-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError("A exportação falhou. Tente reduzir o período."); } finally { setExporting(false); }
  };

  const toggleSort = (field) => {
    setSort((s) => s.field === field ? { field, order: s.order === "desc" ? "asc" : "desc" } : { field, order: "desc" });
  };

  const totalPages = data ? Math.max(1, data.total_pages || 1) : 1;

  const Th = ({ field, children, right }) => (
    <th className={`px-3 py-3 font-medium cursor-pointer select-none ${right ? "text-right" : ""}`} onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-1">{children}{sort.field === field && <span className="text-[var(--purple-bright)]">{sort.order === "desc" ? "↓" : "↑"}</span>}</span>
    </th>
  );

  return (
    <div>
      <PageHeader title="Histórico de eventos" meta="Busca, filtros e paginação executados no servidor. Exportação limitada a 100 mil linhas por arquivo.">
        <Button variant="outline" onClick={exportCsv} disabled={exporting || !data || data.total === 0} data-testid="export-csv">
          <Download size={14} /> {exporting ? "Exportando…" : "Exportar CSV"}
        </Button>
      </PageHeader>

      <FilterBar value={filter} onChange={setFilter} show={{ device: true, type: true, game: true, campaign: true }} />
      {error && <ErrorState message={error} onRetry={load} />}

      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted2)]" />
            <input data-testid="events-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar jogo, plataforma, provedor..." className="mia-input !pl-9 !h-8 !py-0 max-w-md" aria-label="Buscar eventos" />
          </div>
          {data && <span className="mono text-[12px] text-[var(--muted)]" data-testid="events-total">{fmtNum(data.total)} eventos</span>}
        </div>
      </div>

      <Panel>
        {loading ? (
          <div className="p-5 space-y-2">{Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : !data || data.rows.length === 0 ? (
          <EmptyState title="Nenhum evento encontrado" description="Ajuste os filtros ou aguarde a coleta de novos eventos no site público." />
        ) : (
          <>
            <div className="overflow-x-auto mia-scroll">
              <table className="ops-table ops-stack" data-testid="events-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[var(--muted)]">
                    <Th field="server_ts">Data / Hora</Th>
                    <Th field="type">Evento</Th>
                    <Th field="game_name">Jogo</Th>
                    <Th field="platform">Plataforma</Th>
                    <Th field="provider">Provedor</Th>
                    <th className="px-3 py-3 font-medium">Dispositivo</th>
                    <th className="px-5 py-3 font-medium">Origem</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.event_id || i} className="mia-row">
                      <td className="px-3 py-2.5 mono text-[var(--muted)] whitespace-nowrap">{fmt(r.server_ts)}</td>
                      <td className="px-3 py-2.5"><Badge tone="accent">{EVENT_TYPE_LABELS[r.type] || r.type}</Badge></td>
                      <td className="px-3 py-2.5 text-[var(--text)] max-w-[160px] truncate">{r.game_name || "—"}</td>
                      <td className="px-3 py-2.5 text-[var(--muted)]">{r.platform ? platformLabel(r.platform) : "—"}</td>
                      <td className="px-3 py-2.5 text-[var(--muted)]">{PROVIDER_NAMES[r.provider] || r.provider || "—"}</td>
                      <td className="px-3 py-2.5 text-[var(--muted)] capitalize">{r.device || "—"}</td>
                      <td className="px-5 py-2.5 text-[var(--muted2)] text-xs max-w-[140px] truncate">{r.source_step || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Panel>
    </div>
  );
}
